import {
  assignmentRoleLabel,
  ORDER_TYPE_LABELS,
  type AssignmentRole,
  type EmploymentType,
  type OrderType,
  type Personnel,
  type PersonnelAssignment,
  type PersonnelOrder,
} from '@/lib/types';
import { isWorking } from '@/lib/personnel';
import { defaultAllowanceEligible, roleOfOrderType } from '@/pages/hr/usePlacementOrders';

/**
 * 전술판의 <b>계산</b>. 그리는 일은 {@link PlacementBoard} 가 하고, 여기서는 화면을 모른다.
 *
 * <p>나눈 이유: 이 파일의 규칙 두 개가 조용히 틀리면 <b>사람의 본직이 사라진다</b>
 * ({@link targetAssignmentId}) 거나 <b>겸직이 하나로 덮인다</b>({@link addPlaces}). 둘 다 화면을
 * 눌러 봐서는 알기 어렵고, 발령을 확정한 뒤에야 배치표에서 드러난다. 순수 함수로 두면 시험할 수 있다
 * (`placementModel.test.ts`).
 */

export type BoardSection = { id: string; name: string; type: 'department' | 'site' };

/** 아직 서버에 가지 않은 이동 한 건. */
export type Pending = {
  key: string;
  personnelId: string;
  personName: string;
  /** 끌어 온 카드의 배치. 새로 얹는 겸직·파견이면 null. */
  sourceAssignmentId: string | null;
  /** 그 배치의 성격. 종류를 바꿨을 때 <b>옮기는 것인지 더하는 것인지</b>를 이 값으로 가른다. */
  sourceRole: AssignmentRole | null;
  fromName: string | null;
  /** 도착지. 해제(RELEASE)면 null. */
  toName: string | null;
  toType: 'department' | 'site';
  orderType: OrderType;
  roleTitle: string | null;
  fieldName: string | null;
  teamLead: boolean;
  /** 고용 구분 — 정규·사무보조·고졸·PJ직(V68). 배치 성격과 다른 축이다. */
  employmentType: EmploymentType;
  endDate: string | null;
  /** null 이면 서버 기본 규칙을 따른다(파견·현장 발령 = 대상). */
  allowanceEligible: boolean | null;
  /** 겸직 실근무 확인 — 공사전략팀장 · 겸직현장 현장대리인(V590). 겸직 발령에서만 쓴다. */
  workCheckStrategy?: boolean;
  workCheckSiteAgent?: boolean;
};

/** 판 위의 카드 한 장. `pending` 이 붙어 있으면 아직 확정되지 않은 이동이다. */
export type Card = {
  key: string;
  person: Personnel;
  assignmentId: string | null;
  /** 이 카드가 <b>원래</b> 있던 소속. 초안 카드를 다시 끌어도 출발지는 처음 자리로 남는다. */
  fromName: string | null;
  /** 이 카드가 선 자리. 미배치 칸의 카드는 null. */
  sectionName: string | null;
  role: AssignmentRole;
  roleTitle: string | null;
  fieldName: string | null;
  teamLead: boolean;
  employmentType: EmploymentType;
  /**
   * 이 사람이 <b>동시에 서 있는 다른 자리</b>. 겸직·파견이 있으면 본직 카드에도 그 사실을 적는다 —
   * 카드가 두 장이라는 것을 겸직 카드 쪽에서만 알리면, 본직 칸만 보는 사람은 그 인원을 온전히
   * 자기 팀 사람으로 센다.
   */
  alsoAt: Array<{ name: string; role: AssignmentRole; roleTitle: string | null }>;
  pending: Pending | null;
  /**
   * 확정됐지만 <b>아직 오지 않은</b> 발령이 만든 카드(V247). 그날이 오면 진짜 카드가 된다.
   *
   * <p>{@link pending}(브라우저에만 있는 초안)과 다른 축이다 — 초안은 아직 아무 일도 아니고 되돌리려면
   * 지우면 되지만, 예정은 <b>이미 확정된 사실</b>이라 되돌리려면 발령을 취소해야 한다. 그래서 판에서
   * 끌 수 없다.
   */
  scheduled: { effectiveDate: string; orderType: OrderType; orderedByName: string | null } | null;
  /** 예정된 해제·전보로 <b>이 카드가 그날 사라진다</b>. 값은 그 날짜다. */
  leavingOn: string | null;
};

export type Board = { bySection: Map<string, Card[]>; unassigned: Card[] };

/**
 * 서버로 보낼 <b>대상 배치</b>. 비어 있으면 서버는 줄을 새로 만들고, 차 있으면 그 줄을 고친다.
 *
 * <p>여기가 이 화면에서 가장 틀리기 쉬운 자리다. 본직 카드를 끌어다 놓고 종류를 「겸직」으로 바꾸면
 * 뜻은 <b>"본직은 그대로 두고 겸직을 하나 더"</b> 인데, 끌어 온 배치 id 를 그대로 보내면 서버는
 * 본직 줄을 겸직으로 <b>바꿔 버린다</b> — 그 사람의 본직이 조용히 사라진다.
 *
 * <p>그래서 성격이 바뀌면 대상을 비운다. 성격이 같으면(겸직 카드를 다른 현장으로) 그 줄을 옮긴다.
 * 해제는 언제나 대상이 있어야 한다 — 풀 줄을 가리키는 것이 해제의 본문이다.
 */
export function targetAssignmentId(pending: Pending): string | null {
  if (pending.orderType === 'RELEASE') return pending.sourceAssignmentId;
  return roleOfOrderType(pending.orderType) === pending.sourceRole ? pending.sourceAssignmentId : null;
}

/**
 * 초안 한 건의 열쇠. <b>사람 × 도착지 × 종류</b>다.
 *
 * <p>도착지를 넣지 않으면 겸직 둘째를 걸 때 첫째가 덮인다 — 겸직은 여러 곳에 동시에 걸 수 있고,
 * 그것이 겸직의 정의다. 종류까지 넣는 이유는 같은 곳에 겸직과 파견을 함께 걸 수는 없어서다
 * (한 소속에 한 줄 — 서버의 유니크 제약과 같은 규칙).
 */
export function pendingKey(
  personnelId: string,
  toName: string | null,
  orderType: OrderType,
  sourceAssignmentId: string | null = null,
): string {
  // 해제는 도착지가 없다. 사람만으로 열쇠를 만들면 겸직 둘을 한꺼번에 풀 때 뒤엣것이 앞엣것을
  // 덮어 <b>한 곳만 풀린다</b> — 그래서 푸는 배치의 id 로 가른다.
  return toName === null
    ? `${personnelId}::(해제)::${sourceAssignmentId ?? ''}`
    : `${personnelId}::${toName}::${orderType}`;
}

/**
 * 카드를 놓았을 때의 기본 발령 종류.
 *
 * 본직 카드를 옮기면 전보다 — 판에서 카드를 옮기는 동작의 뜻은 대개 "이 사람을 저기로 보낸다" 이기
 * 때문이다. 겸직·파견 카드는 그 성격을 유지한 채 자리만 바꾼다. 겸직을 <b>새로</b> 얹는 일은
 * 카드를 눌러 여는 자리 관리 창에서 한다 — 끌어놓기 하나에 두 뜻을 담으면 어느 쪽인지 알 수 없다.
 */
export function defaultOrderType(role: AssignmentRole, toName: string | null): OrderType {
  if (toName === null) return 'RELEASE';
  if (role === 'concurrent') return 'CONCURRENT';
  if (role === 'dispatch') return 'DISPATCH';
  return 'TRANSFER';
}

/**
 * 자리 <b>여러 곳</b>을 한 번에 더한다 — 겸직 3곳도 한 번의 조작으로.
 *
 * <p>겸직은 하나만 있을 이유가 없다. 본사 소속이 두 현장을 함께 맡는 일이 흔하고, 그때 사람이
 * 하는 생각은 "이 사람을 A·B·C 에 겸직으로 건다" 는 <b>한 번의 결정</b>이다. 끌어놓기를 세 번
 * 시키면 그중 하나를 빠뜨려도 아무도 모른다.
 *
 * <p>이미 서 있는 자리는 걸러 낸다 — 서버의 유니크 제약(사람 × 소속종류 × 소속명)에 걸려 발령
 * 확정 전체가 409 로 죽는다. 여기서 조용히 빼는 편이 낫고, 화면은 그 자리를 아예 못 고르게 한다.
 */
export function addPlaces(
  current: Pending[],
  person: Personnel,
  targets: BoardSection[],
  orderType: Extract<OrderType, 'CONCURRENT' | 'DISPATCH' | 'TRANSFER'>,
  options: {
    from: string | null;
    sourceAssignmentId?: string | null;
    sourceRole?: AssignmentRole | null;
    /** 새 자리에서 맡을 일. 비우면 빈 채로 두고 변경사항 줄에서 채운다. */
    roleTitle?: string | null;
    fieldName?: string | null;
    employmentType?: EmploymentType;
  },
): Pending[] {
  const added = targets
    .filter((target) => !current.some((pending) => pending.key === pendingKey(person.id, target.name, orderType)))
    .map<Pending>((target) => ({
      key: pendingKey(person.id, target.name, orderType),
      personnelId: person.id,
      personName: person.name,
      sourceAssignmentId: options.sourceAssignmentId ?? null,
      sourceRole: options.sourceRole ?? null,
      fromName: options.from,
      toName: target.name,
      toType: target.type,
      orderType,
      /*
        <b>대표 직책(person.position)을 자동으로 넣지 않는다.</b> 본사 「공무」 인 사람을 현장에 겸직으로
        걸면 현장 카드에도 「공무」 가 붙는데, 현장에서 맡는 일은 대개 다르다(현장대리인·안전 등).
        자동으로 채운 값은 <b>사람이 정한 값처럼 보여서</b> 아무도 고치지 않는다 — 빈 채로 두면
        변경사항 줄에서 눈에 띈다.
      */
      roleTitle: options.roleTitle?.trim() || null,
      fieldName: options.fieldName?.trim() || person.field || null,
      // 팀장은 <b>그 팀에서의 자리</b>다. 사람을 따라 옮겨 다니지 않는다.
      teamLead: false,
      employmentType: options.employmentType ?? 'REGULAR',
      endDate: null,
      allowanceEligible: defaultAllowanceEligible(orderType, target.type),
    }));

  return [...current, ...added];
}

/**
 * 이 소속의 팀장(초안 포함). 새 팀장을 세울 때 <b>이미 있는 팀장</b>을 알려 주는 데 쓴다.
 *
 * <p>막지는 않는다. 인수인계 기간에 팀장이 둘인 순간이 실제로 있고, 막으면 그 상태를 적을 방법이
 * 없어진다. 다만 모르고 둘이 되는 것과 알고 둘로 두는 것은 다르다.
 */
export function teamLeadAt(board: Board, sectionName: string, exceptPersonnelId?: string): Card | null {
  return (
    (board.bySection.get(sectionName) ?? []).find(
      (card) => card.teamLead && card.person.id !== exceptPersonnelId,
    ) ?? null
  );
}

/**
 * 역할·분야 제안 목록. 인력배치표 <b>명부</b>가 사람을 세우는 순서와 같은 표다
 * (`PlacementPage` 의 정렬 상수) — 판에서 아무 말이나 적을 수 있게 두면 명부의 정렬이 무너진다.
 *
 * <p>강제하지는 않는다({@code <datalist>} 제안이다). 조직마다 부르는 이름이 다르고, 새 역할이
 * 생길 때마다 코드를 고치게 만들 이유가 없다.
 */
export const ROLE_SUGGESTIONS: Record<'department' | 'site', string[]> = {
  department: ['팀장', '파트장', '공무', '기술', '관리'],
  /*
    현장의 역할은 많다(2026-08-18 확장). 이 배열은 <b>제안 목록이자 명부의 정렬 순서</b>이므로,
    적는 차례가 곧 배치표에서 서는 차례다 — 현장을 이끄는 자리(총괄소장·소장·대리인)가 앞이고,
    그다음이 공사를 굴리는 자리(공무·공사·토목), 만드는 자리(설계·SHOP·BIM·디자인),
    보는 자리(품질·안전), 뒷받침하는 자리(사무) 순이다.

    ★ 목록에 <b>없는 역할도 그대로 저장된다</b>(제안일 뿐 강제가 아니다). 새 직종이 생기면 화면에서
      직접 적으면 되고, 그 값은 이 목록의 <b>뒤</b>에 선다(`orderIndex` 가 못 찾은 값을 뒤로 보낸다).
      자주 쓰이기 시작하면 그때 이 배열에 자리를 잡아 주면 된다 — 코드를 고쳐야 입력이 가능한
      구조로 두지 않는다.
  */
  site: [
    // 현장의 장은 현장소장, 없으면 현장대리인이다(현장소장 복원 2026-10-28 · V591).
    '총괄소장', '현장소장', '현장대리인',
    '공무', '공사', '토목',
    '설계', 'SHOP', 'BIM', '디자인',
    '품질', '안전',
    '사무',
  ],
};

/** 명부의 분야 정렬과 같은 순서. */
export const FIELD_SUGGESTIONS = ['건축', '토목', '조경', '기계', '전기', '품질', '안전'];

// ── 사람 세우는 순서 ──────────────────────────────────────────────────────────

/**
 * 직급 서열(높은 순). 서버 {@code ref_positions.rank_order} 와 <b>같은 줄세우기</b>여야 한다
 * (회장 130 · 부회장 120 · 사장 110 · 대표이사 100 … 사원 10 — V16 + V66(본부장) + V68 §5).
 *
 * <p>둘이 어긋나면 배치표의 순서와 결재선·권한 판정이 서로 다른 말을 한다. 서버 값을 내려받지 않는
 * 이유는 직급 목록이 <b>결재 메뉴 권한 뒤에</b> 있어서다({@code GET /approval/positions}) — 배치표를
 * 보는 사람이 그 권한을 갖고 있으리라는 보장이 없다. 직급이 늘면 이 배열도 함께 고친다.
 */
export const RANK_ORDER = [
  '회장', '부회장', '사장', '대표이사', '대표', '부사장', '전무', '상무', '본부장', '이사',
  '현장대리인', '부장', '차장', '과장', '대리', '주임', '기사', '사원',
];

/**
 * 임원급이 끝나는 자리. 사다리 앞쪽(회장 … 이사)이 임원이고, 그 아래(현장대리인부터)는 맡은 자리가
 * 서열보다 먼저다 — <b>경영진에서만</b> 직급이 팀장 표시를 앞지르게 하는 데 쓴다({@link compareMembers}).
 */
const EXECUTIVE_RANK_END = RANK_ORDER.indexOf('현장대리인');

/**
 * 사다리에서의 자리 — <b>직급칸을 먼저, 비면 직위칸으로</b>.
 *
 * <p>경영진은 「회장·사장」 을 직급칸에 적기도 하고 직위칸에 적기도 한다. 직급칸만 보면 그 사람들이
 * 「직급을 안 적은 사람」 으로 잡혀 맨 뒤로 밀리는데, 오류가 나지 않아 화면을 봐야 안다. 서버 조직도
 * 조인({@code DirectoryMapper.rankLadder})과 같은 폴백이다.
 */
function ladderIndex(rank: string | null | undefined, position: string | null | undefined): number {
  const byRank = orderIndex(RANK_ORDER, rank);
  return byRank < RANK_ORDER.length ? byRank : orderIndex(RANK_ORDER, position);
}

/**
 * <b>우두머리 표시</b>. 팀장 칸이 켜져 있거나, 맡은 일이 소장·팀장이면 그 소속의 맨 위에 선다.
 *
 * <p>직급으로는 안 된다 — 현장대리인이 부장일 수도 차장일 수도 있고, 팀장이 그 팀에서 가장 높은
 * 직급이 아닌 경우도 흔하다. <b>자리가 사람보다 먼저</b>다.
 */
export function isLead(teamLead: boolean, roleTitle: string | null | undefined): boolean {
  const title = roleTitle ?? '';
  // 「현장대리인」 에는 '소장' 이 없다(2026-08-18 개명). 이 한 줄을 빠뜨리면 현장을 이끄는 사람이
  // 명부·판에서 맨 위에 서지 않는데, 오류가 나지 않아 한동안 아무도 모른다.
  return teamLead
    || title.includes('소장')
    || title.includes('대리인')
    || title.includes('팀장')
    || title.includes('본부장');
}

/**
 * 목록에서의 자리. 목록에 없는 값은 뒤로 보낸다 — 직급을 안 적은 사람이 위로 올라오면 안 된다.
 *
 * <h2>정확히 같은 이름을 먼저 본다</h2>
 * 부분 일치만으로 찾으면 <b>부회장이 회장으로, 부사장이 사장으로</b> 잡힌다 — 「부회장」 은 「회장」 을
 * 포함하기 때문이다. 그 순간 부회장이 회장보다 위에 서거나(목록 순서에 따라) 둘이 같은 자리로
 * 뭉개진다. 그래서 <b>완전 일치를 먼저</b> 찾고, 없을 때만 부분 일치로 물러선다.
 *
 * <p>부분 일치를 남겨 두는 이유는 실제 자료가 깨끗하지 않아서다 — 「부장(대우)」·「과장 A」 처럼
 * 적힌 줄이 있고, 그것들은 뒤로 밀리는 것보다 제 자리에 서는 편이 낫다.
 */
export function orderIndex(order: string[], value: string | null | undefined): number {
  const text = (value ?? '').trim();
  if (!text) return order.length;

  const exact = order.indexOf(text);
  if (exact >= 0) return exact;

  const partial = order.findIndex((candidate) => text.includes(candidate));
  return partial < 0 ? order.length : partial;
}

/** 한 소속 안에서 사람을 세우는 데 필요한 것만. 명부의 행과 판의 카드가 이 모양으로 맞춰 들어온다. */
export type Member = {
  name: string;
  rank: string | null | undefined;
  roleTitle: string | null | undefined;
  /** 직위칸. 직급칸이 비었을 때 사다리를 여기서 찾는다({@link ladderIndex}) — 정렬에만 쓴다. */
  position?: string | null;
  fieldName?: string | null;
  teamLead: boolean;
};

/**
 * 한 소속 안의 순서 — <b>①팀장·소장 ②직급 높은 순 ③분야 ④이름</b>.
 *
 * <p>명부(인쇄되는 문서)와 전술판이 <b>같은 함수</b>를 쓴다. 전에는 판이 저장 순서(`sort_order`)로만
 * 서 있어서, 같은 현장을 명부에서 보면 소장이 맨 위인데 판에서는 가운데 있었다 — 두 화면이 같은
 * 자료를 다르게 세우면 어느 쪽이 조직도인지 알 수 없다.
 *
 * <p>분야를 직급 <b>뒤</b>로 내렸다(2026-08-18). 예전에는 현장 명부가 분야로 먼저 묶여서, 건축 사원이
 * 토목 부장보다 위에 섰다. 배치표를 읽는 사람이 먼저 찾는 것은 "누가 위인가" 이지 "무슨 공종인가" 가
 * 아니다 — 분야는 같은 직급끼리의 순서로 남긴다.
 *
 * <h2>임원이 끼면 직급이 먼저다 (2026-09-28)</h2>
 * 「경영진 정렬을 회장 > 사장 > 부사장 > 전무 > 상무 > 부장 으로」. 경영진 부서에서 부장이 팀장 칸을
 * 켜고 있으면 <b>회장 위에</b> 섰다 — 우두머리 규칙이 사다리를 앞질러서다. 임원급(회장 … 이사)이
 * 한쪽이라도 끼면 직급을 먼저 보고, 팀장 표시는 같은 직급끼리의 순서로 내린다. 그 아래에서는
 * 예전 규칙이 그대로다: 현장대리인이 차장이어도 그 현장의 부장보다 위에 선다.
 */
export function compareMembers(a: Member, b: Member): number {
  const leadA = isLead(a.teamLead, a.roleTitle);
  const leadB = isLead(b.teamLead, b.roleTitle);

  const rankA = ladderIndex(a.rank, a.position);
  const rankB = ladderIndex(b.rank, b.position);
  const byRank = rankA - rankB;

  // 임원이 한쪽이라도 있으면 사다리가 먼저, 아니면 맡은 자리가 먼저.
  if (Math.min(rankA, rankB) < EXECUTIVE_RANK_END) {
    if (byRank !== 0) return byRank;
    if (leadA !== leadB) return leadA ? -1 : 1;
  } else if (leadA !== leadB) {
    return leadA ? -1 : 1;
  }

  if (byRank !== 0) return byRank;

  const byField = orderIndex(FIELD_SUGGESTIONS, a.fieldName) - orderIndex(FIELD_SUGGESTIONS, b.fieldName);
  if (byField !== 0) return byField;

  return a.name.localeCompare(b.name, 'ko');
}

/** 판의 카드를 사람 세우는 순서로. {@link project} 가 칸마다 한 번씩 부른다. */
function compareCards(a: Card, b: Card): number {
  return compareMembers(
    {
      name: a.person.name, rank: a.person.rank, position: a.person.position,
      roleTitle: a.roleTitle, fieldName: a.fieldName, teamLead: a.teamLead,
    },
    {
      name: b.person.name, rank: b.person.rank, position: b.person.position,
      roleTitle: b.roleTitle, fieldName: b.fieldName, teamLead: b.teamLead,
    },
  );
}

/**
 * 서버 배치 + 초안 → 판 위의 카드.
 *
 * 전보는 카드를 옮기고, 겸직·파견은 도착지에 카드를 <b>더한다</b>. 해제는 카드를 뺀다.
 * 어느 소속에도 카드가 없는 재직자는 미배치 칸으로 모인다.
 */
export function project(
  personnel: Personnel[],
  assignments: PersonnelAssignment[],
  personById: Map<string, Personnel>,
  pendings: Pending[],
  scheduled: PersonnelOrder[] = [],
): Board {
  const bySection = new Map<string, Card[]>();
  const placed = new Set<string>();

  const push = (name: string, card: Card) => {
    bySection.set(name, [...(bySection.get(name) ?? []), card]);
    placed.add(card.person.id);
  };

  /*
    예정 발령이 <b>그날 비울</b> 자리. 전보는 있던 줄을 옮기는 일이라 출발지 카드가 사라지고,
    해제는 그 줄을 지운다. 겸직·파견을 얹는 예정은 아무 자리도 비우지 않는다(source 가 없다).
  */
  const leavingOn = new Map<string, string>();
  for (const order of scheduled) {
    if (order.sourceAssignmentId && order.orderType !== 'CONCURRENT') {
      leavingOn.set(order.sourceAssignmentId, order.effectiveDate);
    }
  }

  // 서버가 고쳐 쓸 줄(= 대상 배치가 있는 초안)은 판에서 원래 자리를 뜬다. 본직을 둔 채 겸직을
  // 더하는 초안은 대상이 비어 있으므로(→ targetAssignmentId) 본직 카드가 그대로 남는다.
  const movedAway = new Set(pendings.map(targetAssignmentId).filter((id): id is string => id !== null));

  for (const assignment of assignments) {
    const person = personById.get(assignment.personnelId);
    if (!person || !isWorking(person)) continue;
    if (movedAway.has(assignment.id)) continue; // 전보·해제로 이 자리를 떠난다

    push(assignment.assignmentName, {
      key: assignment.id,
      person,
      assignmentId: assignment.id,
      fromName: assignment.assignmentName,
      sectionName: assignment.assignmentName,
      role: assignment.assignmentRole ?? 'primary',
      roleTitle: assignment.roleTitle,
      fieldName: assignment.fieldName,
      teamLead: assignment.teamLead,
      employmentType: assignment.employmentType ?? 'REGULAR',
      alsoAt: [],
      pending: null,
      scheduled: null,
      leavingOn: leavingOn.get(assignment.id) ?? null,
    });
  }

  /*
    확정된 <b>예정</b> 발령을 도착지에 점선으로 세운다(V247). 진짜 배치가 아니므로
    ① `placed` 에 넣지 않는다 — 오늘 아무 데도 없는 사람은 오늘 미배치 칸에 있어야 한다.
    ② `alsoAt` 계산에서 빠진다(withAlsoAt) — 「이 사람 저기에도 있음」 은 지금 이야기다.
    해제는 도착지가 없어 카드가 서지 않는다. 대신 출발지 카드에 `leavingOn` 이 붙는다.
  */
  for (const order of scheduled) {
    if (!order.toName) continue;
    const person = personById.get(order.personnelId);
    if (!person || !isWorking(person)) continue;

    bySection.set(order.toName, [
      ...(bySection.get(order.toName) ?? []),
      {
        key: `scheduled-${order.id}`,
        person,
        assignmentId: null,
        fromName: order.fromName,
        sectionName: order.toName,
        role: roleOfOrderType(order.orderType) ?? 'primary',
        roleTitle: order.roleTitle,
        fieldName: order.fieldName,
        teamLead: order.teamLead,
        employmentType: order.employmentType,
        alsoAt: [],
        pending: null,
        scheduled: {
          effectiveDate: order.effectiveDate,
          orderType: order.orderType,
          orderedByName: order.orderedByName,
        },
        leavingOn: null,
      },
    ]);
  }

  for (const pending of pendings) {
    if (!pending.toName) continue; // 해제는 도착지가 없다
    const person = personById.get(pending.personnelId);
    if (!person) continue;

    push(pending.toName, {
      key: `pending-${pending.key}`,
      person,
      assignmentId: pending.sourceAssignmentId,
      fromName: pending.fromName,
      sectionName: pending.toName,
      role: roleOfOrderType(pending.orderType) ?? 'primary',
      roleTitle: pending.roleTitle,
      fieldName: pending.fieldName,
      teamLead: pending.teamLead,
      employmentType: pending.employmentType,
      alsoAt: [],
      pending,
      scheduled: null,
      leavingOn: null,
    });
  }

  const unassigned = personnel
    .filter((person) => isWorking(person) && !placed.has(person.id))
    .map<Card>((person) => ({
      key: `free-${person.id}`,
      person,
      assignmentId: null,
      fromName: null,
      sectionName: null,
      role: 'primary',
      roleTitle: person.position,
      fieldName: person.field,
      teamLead: false,
      employmentType: 'REGULAR',
      alsoAt: [],
      pending: null,
      scheduled: null,
      leavingOn: null,
    }))
    .sort(compareCards);

  return { bySection: withAlsoAt(bySection), unassigned };
}

/**
 * 한 사람이 여러 칸에 서 있으면 <b>카드마다 나머지 자리를 적어 준다</b>.
 *
 * <p>겸직 카드에만 뱃지를 다는 것으로는 부족하다. 인사총무팀 칸만 보는 사람에게 그 카드는 평범한
 * 팀원으로 보이고, 그 사람이 현장에도 반쯤 나가 있다는 사실은 다른 칸에 있다 — 칸마다 사람을 세면
 * 총원이 부풀고, 자리를 옮길 때 "이 사람 이미 두 곳" 을 모른 채 옮기게 된다.
 */
function withAlsoAt(bySection: Map<string, Card[]>): Map<string, Card[]> {
  const byPerson = new Map<string, Card[]>();
  for (const cards of bySection.values()) {
    for (const card of cards) {
      // 예정 카드는 세지 않는다 — 「이 사람 저기에도 있음」 은 지금 이야기다(V247).
      if (card.scheduled) continue;
      byPerson.set(card.person.id, [...(byPerson.get(card.person.id) ?? []), card]);
    }
  }

  const filled = new Map<string, Card[]>();
  for (const [name, cards] of bySection) {
    filled.set(
      name,
      // 세우는 순서는 명부와 같다(compareMembers) — 판만 저장 순서로 두면 같은 현장이 두 화면에서
      // 다르게 읽힌다. 정렬은 alsoAt 을 채운 <b>뒤</b>에 한다: 비교에 쓰는 값이 카드 자신의 것이라
      // 순서와 무관하지만, 뒤집어 두면 나중에 「겸직이면 아래로」 같은 규칙을 넣을 때 함정이 된다.
      cards.map((card) => ({
        ...card,
        alsoAt: (byPerson.get(card.person.id) ?? [])
          .filter((other) => other.key !== card.key && other.sectionName)
          // 맡은 일은 자리마다 다르다 — 본사에서는 공무, 현장에서는 안전일 수 있다. 칸이 좁아
          // 카드에는 소속만 적고, 담당까지는 마우스를 올렸을 때 보여 준다.
          .map((other) => ({ name: other.sectionName as string, role: other.role, roleTitle: other.roleTitle })),
      })).sort(compareCards),
    );
  }
  return filled;
}

/** 이 사람이 지금(초안 포함) 서 있는 카드 전부. 자리 관리 창이 목록으로 그린다. */
export function placesOf(board: Board, personnelId: string): Card[] {
  return [...board.bySection.values()].flat().filter((card) => card.person.id === personnelId);
}

/**
 * 이동 한 줄을 사람 말로. <b>본직을 두고 겸직을 더하는 것</b>과 <b>본직을 옮기는 것</b>은 화살표만으로
 * 구분되지 않는다 — 전자는 원래 자리가 그대로이므로 그렇게 적는다.
 */
export function changeSummary(pending: Pending): string {
  const label = ORDER_TYPE_LABELS[pending.orderType];
  if (pending.orderType === 'RELEASE') return `${pending.fromName ?? '미배치'} 배치 해제`;
  if (targetAssignmentId(pending) === null && pending.fromName) {
    return `${pending.fromName} 유지 + ${pending.toName} ${label}`;
  }
  return `${pending.fromName ?? '미배치'} → ${pending.toName} (${label})`;
}

/** 마우스를 올렸을 때의 설명 — 이 사람이 몇 곳에 어떻게 서 있는지 한 번에 읽힌다. */
export function cardTitle(card: Card): string {
  const here = card.sectionName ? (card.pending?.toType ?? 'site') : 'department';
  const place = (name: string, role: AssignmentRole, roleTitle: string | null) =>
    `${assignmentRoleLabel(role, here)} ${name}${roleTitle ? ` · ${roleTitle}` : ''}`;

  const lines = [
    card.person.name,
    place(card.sectionName ?? '미배치', card.role, card.roleTitle),
    ...card.alsoAt.map((other) => place(other.name, other.role, other.roleTitle)),
  ];
  if (card.alsoAt.length > 0) lines.push(`— 자리 ${card.alsoAt.length + 1}곳`);
  if (card.pending) lines.push('확정 전 초안입니다');
  return lines.join('\n');
}

/**
 * 겸직·파견의 색. 본직은 색을 갖지 않는다 — 카드의 대부분이 본직이라, 거기에까지 색을 주면
 * 색이 뜻을 잃고 판이 알록달록해지기만 한다.
 */
export const ROLE_COLOR: Record<AssignmentRole, string> = {
  primary: 'var(--text-muted)',
  concurrent: '#7c3aed',
  dispatch: '#ea580c',
};
